// Le regole pure dell'ingresso di un Lead (piano dell'ingresso del 10 ottobre 2026, I1, Task 1; piano M6, Task 14, D14 e D17;
// INGR-4, INGR-6, INGR-8, SEQ-5): ogni ingresso, da qualunque sorgente, passa da un solo nucleo del server (`enterLead`), che
// cerca il Lead per telefono e qui chiede tre cose:
// - che cosa un ingresso cambia di un Lead che c'è già (`mergeEntry`): soltanto i campi dell'ingresso, mai owner, status,
//   chiamate, note, Task od Opportunità; un valore nuovo non vuoto sostituisce quello scritto, un valore vuoto non cancella mai,
//   un valore uguale a meno di maiuscole, accenti e spazi non è nuovo (resta quello scritto); con «Completa solo i campi vuoti»
//   soltanto dove non c'è niente. I tag si aggiungono senza doppioni (per nome normalizzato, come la chiave dei tag) e nessuno si
//   toglie;
// - chi diventa owner di un Lead nuovo dal tag «Owner: Nome Cognome» (`ownerFromTags`): una persona attiva del team, che può
//   essere owner; un nome sconosciuto, di una persona sospesa o che non può essere owner, di due persone, o due tag con due persone
//   diverse lasciano decidere la regola di assegnazione, e il motivo resta; il tag «Owner: …» non resta mai fra i tag del Lead;
// - se la sequenza di chiamate parte con il rientro (`shouldRestartSequence`): per un Lead che non ne ha mai avuta una, o dopo una
//   sequenza finita con tutti i tentativi senza risposta (`exhausted`, «non raggiunto»), e soltanto se nulla è in agenda e lo status
//   non blocca le chiamate. Le altre fini (una risposta, una fermata a mano, uno status che blocca, il numero errato, l'archivio,
//   l'unione) non ripartono da sole: resta «Riparti con la sequenza», come dice la ripartenza della sequenza sul server.
// Niente database e niente sorgenti: `meta`, `web`, `ghl`, `file` e `manual` sono identificativi dei cinque ingressi; i loro
// lettori stanno negli adattatori del server.
import { normalizeSearchText } from "./search-text.js";
/**
 * Da dove entra un Lead: i moduli di Meta con il collegamento diretto (`meta`), i moduli del sito e delle landing (`web`), le landing
 * dello strumento delle email finché restano (`ghl`), un file importato (`file`), «Nuovo Lead» (`manual`).
 */
export const LEAD_ENTRY_SYSTEMS = ["meta", "web", "ghl", "file", "manual"];
/**
 * Com'è andato un ingresso: il Lead è nato (`created`), c'era già e l'ingresso l'ha raggiunto (`updated`, anche senza nessun dato
 * nuovo: l'arrivo stesso conta), non è entrato nessuno (`discarded`, con il motivo), lo stesso arrivo c'era già (`duplicate`).
 */
export const LEAD_ENTRY_RESULTS = ["created", "updated", "discarded", "duplicate"];
/** I campi della scheda che un ingresso porta, oltre al telefono che lo riconosce e ai tag. */
export const ENTRY_FIELDS = ["firstName", "lastName", "salonName", "email", "cityIstatCode"];
/** Testo di una riga: senza spazi ai bordi né doppi; vuoto vale nessun valore (come la scheda). */
export function cleanEntryText(value) {
    if (typeof value !== "string")
        return null;
    const text = value.trim().replace(/\s+/g, " ");
    return text === "" ? null : text;
}
/** La chiave di un tag: il nome senza maiuscole, accenti e segni (la stessa della colonna `name_key` dei tag). */
export function entryTagKey(name) {
    return normalizeSearchText(name);
}
/** Due valori uguali a meno di maiuscole, accenti e spazi. */
const sameText = (left, right) => normalizeSearchText(left) === normalizeSearchText(right);
/** I nomi dei tag ripuliti e senza doppioni (per chiave), nell'ordine dell'arrivo; i vuoti non contano. */
function distinctTags(names) {
    const seen = new Set();
    const out = [];
    for (const raw of names) {
        const name = cleanEntryText(raw);
        if (name === null)
            continue;
        const key = entryTagKey(name);
        if (key === "" || seen.has(key))
            continue;
        seen.add(key);
        out.push(name);
    }
    return out;
}
/**
 * Che cosa l'ingresso cambia del Lead che c'è già (in cima al file): i campi nell'ordine di `ENTRY_FIELDS` e i tag da aggiungere,
 * nell'ordine dell'arrivo. Owner e status non sono fra i fatti: un ingresso non li tocca mai.
 */
export function mergeEntry(existing, incoming, mode = "update") {
    const changes = [];
    for (const field of ENTRY_FIELDS) {
        const after = cleanEntryText(incoming[field]);
        if (after === null)
            continue;
        const before = existing[field];
        if (before !== null && (mode === "fill_empty" || sameText(before, after)))
            continue;
        changes.push({ field, before, after });
    }
    const current = new Set(existing.tags.map(entryTagKey));
    const tagsToAdd = distinctTags(incoming.tags ?? []).filter((name) => !current.has(entryTagKey(name)));
    return { changes, tagsToAdd };
}
/**
 * Perché il tag ha deciso o no: `tag` l'ha deciso; `tag_unknown` il nome non è di nessuno (o manca); `tag_ambiguous` è di più
 * persone, o i tag nominano persone diverse; `tag_inactive` la persona non è attiva o non può essere owner; `no_tag` nessun tag
 * «Owner:».
 */
export const OWNER_TAG_REASONS = ["tag", "tag_unknown", "tag_ambiguous", "tag_inactive", "no_tag"];
// «Owner:» seguito dal nome, con maiuscole e spazi qualsiasi; senza i due punti è un tag come gli altri.
const OWNER_TAG = /^owner\s*:\s*(.*)$/i;
/**
 * L'owner di un Lead nuovo dal tag «Owner: Nome Cognome» (in cima al file): l'appartenenza, oppure `null` con il motivo per cui decide
 * la regola; `cleanTags` sono i tag dell'arrivo senza quelli «Owner:», ripuliti e senza doppioni.
 */
export function ownerFromTags(tags, people) {
    const named = new Set();
    let ownerTags = 0;
    const others = [];
    for (const raw of tags) {
        const tag = cleanEntryText(raw);
        if (tag === null)
            continue;
        const match = OWNER_TAG.exec(tag);
        if (match === null) {
            others.push(tag);
            continue;
        }
        ownerTags += 1;
        const name = normalizeSearchText(match[1] ?? "");
        if (name !== "")
            named.add(name);
    }
    const cleanTags = distinctTags(others);
    if (ownerTags === 0)
        return { ownerId: null, reason: "no_tag", cleanTags };
    if (named.size > 1)
        return { ownerId: null, reason: "tag_ambiguous", cleanTags };
    const [name] = [...named];
    if (name === undefined)
        return { ownerId: null, reason: "tag_unknown", cleanTags };
    const matches = people.filter((person) => normalizeSearchText(person.fullName) === name);
    const [person] = matches;
    if (person === undefined)
        return { ownerId: null, reason: "tag_unknown", cleanTags };
    if (matches.length > 1)
        return { ownerId: null, reason: "tag_ambiguous", cleanTags };
    if (!person.active)
        return { ownerId: null, reason: "tag_inactive", cleanTags };
    return { ownerId: person.id, reason: "tag", cleanTags };
}
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
/**
 * Se un nome si può dare a un tag creato a mano (`POST /api/tags`): con almeno una lettera o una cifra (così la chiave `name_key`
 * non è vuota né fatta di soli segni) e senza «Owner:» in testa, che negli ingressi sceglie l'owner e non diventa mai un tag.
 */
export function isUsableTagName(name) {
    return LETTER_OR_DIGIT.test(name) && entryTagKey(name) !== "" && !OWNER_TAG.test(name.trim());
}
/**
 * Se la sequenza parte con il rientro del Lead (in cima al file): `previous` è l'ultima sequenza del Lead (`null` se non ne ha mai
 * avuta una), `pendingBooking` se ha qualcosa in agenda (un Task aperto: un richiamo, una disco, una demo), `statusBlocksCalls` se
 * il suo status blocca le chiamate.
 */
export function shouldRestartSequence(previous, pendingBooking, statusBlocksCalls = false) {
    if (pendingBooking || statusBlocksCalls)
        return false;
    if (previous === null)
        return true;
    return previous.ended && previous.endReason === "exhausted";
}
//# sourceMappingURL=lead-entry.js.map